import { Save } from "lucide-react";
import { FormEvent, useEffect, useState } from "react";
import { today } from "../lib/format";
import type { OrderPayload } from "../types";

const defaultOrder: OrderPayload = {
  platform: "京东",
  category: "空调",
  order_date: today(),
  customer_name: "",
  phone_tail: "",
  product_name: "",
  model: "",
  amount: "",
  payment_status: "已付款",
  logistics_company: "",
  tracking_number: "",
  shipping_status: "未发货",
  eta: "",
  remark: "",
  screenshot_key: "",
};

interface OrderFormProps {
  initialValue?: Partial<OrderPayload> | null;
  submitLabel?: string;
  busy?: boolean;
  onSubmit: (order: OrderPayload) => Promise<void> | void;
}

export function OrderForm({
  initialValue,
  submitLabel = "保存订单",
  busy,
  onSubmit,
}: OrderFormProps) {
  const [form, setForm] = useState<OrderPayload>({
    ...defaultOrder,
    ...initialValue,
  });

  useEffect(() => {
    setForm({ ...defaultOrder, ...initialValue });
  }, [initialValue]);

  function patch(key: keyof OrderPayload, value: string) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    await onSubmit({
      ...form,
      amount: Number(form.amount || 0),
    });
  }

  return (
    <form className="form-grid" onSubmit={handleSubmit}>
      <SelectField
        label="平台"
        value={form.platform}
        onChange={(value) => patch("platform", value)}
        options={["京东", "拼多多", "其他"]}
        required
      />
      <SelectField
        label="大类"
        value={form.category}
        onChange={(value) => patch("category", value)}
        options={["空调", "电视", "洗衣机", "其他"]}
        required
      />
      <TextField
        label="订单日期"
        type="date"
        value={form.order_date}
        onChange={(value) => patch("order_date", value)}
        required
      />
      <TextField
        label="客户姓名"
        value={form.customer_name ?? ""}
        onChange={(value) => patch("customer_name", value)}
      />
      <TextField
        label="手机尾号"
        value={form.phone_tail ?? ""}
        onChange={(value) => patch("phone_tail", value)}
        inputMode="numeric"
      />
      <TextField
        label="商品名称"
        value={form.product_name}
        onChange={(value) => patch("product_name", value)}
        required
      />
      <TextField
        label="型号"
        value={form.model ?? ""}
        onChange={(value) => patch("model", value)}
      />
      <TextField
        label="金额"
        type="number"
        value={String(form.amount ?? "")}
        onChange={(value) => patch("amount", value)}
        min="0"
        step="0.01"
        required
      />
      <SelectField
        label="付款状态"
        value={form.payment_status ?? ""}
        onChange={(value) => patch("payment_status", value)}
        options={["已付款", "未付款", "退款中", "已退款"]}
      />
      <TextField
        label="物流公司"
        value={form.logistics_company ?? ""}
        onChange={(value) => patch("logistics_company", value)}
      />
      <TextField
        label="运单号"
        value={form.tracking_number ?? ""}
        onChange={(value) => patch("tracking_number", value)}
      />
      <SelectField
        label="物流状态"
        value={form.shipping_status ?? ""}
        onChange={(value) => patch("shipping_status", value)}
        options={["未发货", "暂无物流", "已发货", "运输中", "已签收", "异常"]}
      />
      <TextField
        label="预计到达"
        type="date"
        value={form.eta ?? ""}
        onChange={(value) => patch("eta", value)}
      />
      <TextField
        label="截图 Key"
        value={form.screenshot_key ?? ""}
        onChange={(value) => patch("screenshot_key", value)}
      />
      <label className="field field-wide">
        <span>备注</span>
        <textarea
          value={form.remark ?? ""}
          onChange={(event) => patch("remark", event.target.value)}
          rows={4}
        />
      </label>
      <div className="form-actions">
        <button className="primary-button" disabled={busy} type="submit">
          <Save size={17} aria-hidden />
          {busy ? "保存中..." : submitLabel}
        </button>
      </div>
    </form>
  );
}

interface FieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
}

interface TextFieldProps extends FieldProps {
  type?: string;
  min?: string;
  step?: string;
  inputMode?: "numeric" | "decimal" | "text";
}

function TextField({
  label,
  value,
  onChange,
  required,
  type = "text",
  min,
  step,
  inputMode,
}: TextFieldProps) {
  return (
    <label className="field">
      <span>{label}</span>
      <input
        inputMode={inputMode}
        min={min}
        onChange={(event) => onChange(event.target.value)}
        required={required}
        step={step}
        type={type}
        value={value}
      />
    </label>
  );
}

interface SelectFieldProps extends FieldProps {
  options: string[];
}

function SelectField({
  label,
  value,
  onChange,
  options,
  required,
}: SelectFieldProps) {
  return (
    <label className="field">
      <span>{label}</span>
      <select
        onChange={(event) => onChange(event.target.value)}
        required={required}
        value={value}
      >
        <option value="">请选择</option>
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    </label>
  );
}
